import { useState } from 'react';
import { message } from 'antd';
import create from 'zustand';
import moment from 'moment';
import { useQuery } from 'react-query';
import { LAST_20_DAYS, MESSAGES_REFETCH_INTERVAL_MS } from 'consts/common';
import { captureException } from '@sentry/react';
import { MeterRegister } from '@enmon/commons';
import { getLoraApi } from '../api/lora';
import { LoraDeviceMessagesResponse } from '../services/api/lora';
import { QUERY_KEYS } from '../utils/queryKeys';

export interface LoraMessage {
  id: string;
  rssi: number;
  fPort: number;
  datetime: Date;
}

export interface LoraMessagesStore {
  setLoraMessages: (payload?: LoraMessage[]) => void;
  loraMessages?: LoraMessage[];
}

const useLoraMessagesStore = create<LoraMessagesStore>(set => ({
  loraMessages: [],
  setLoraMessages: (payload?: LoraMessage[]) =>
    set(() => ({
      loraMessages: payload,
    })),
}));

interface LoraMessagesShape {
  loraMessages: LoraMessage[] | undefined;
  loading: boolean;
  lastFetchTime: Date | undefined;
}

const loraApi = getLoraApi();

const getParsedMessages = (item: LoraDeviceMessagesResponse['data'][number]) => ({
  id: item.id,
  rssi: Math.max(...item.message.gws.map(gwsItem => gwsItem.rssi)),
  fPort: item.message.port,
  datetime: new Date(item.messageTime),
});

/**
 * TODO: ENMON-1658 Refactor useLoraMessages hook https://enmontech.atlassian.net/browse/ENMON-1658
 * TODO: ENMON-1659 Fix missing signal propagation in useLoraMessages hook
 *    https://enmontech.atlassian.net/browse/ENMON-1658
 */
export const useLoraMessages = (dataSourceId: MeterRegister['dataSourceId']): LoraMessagesShape => {
  const { loraMessages, setLoraMessages } = useLoraMessagesStore();
  const [lastFetchTime, setLastFetchTime] = useState<Date | undefined>(undefined);
  const currentDate = new Date();
  const monthOldDate = moment(currentDate).subtract(LAST_20_DAYS, 'day').toDate();

  const { isLoading, isFetching } = useQuery(
    QUERY_KEYS.lora.messages.all(dataSourceId),
    () =>
      loraApi.getDeviceUpMessages(dataSourceId, {
        to: currentDate,
        from: monthOldDate,
        limit: 10,
        sort: '-messageTime',
      }),
    {
      onError: err => {
        console.error(err);
        captureException(err);
        message.error(`Chyba při načítání Lora zpráv ze serveru`);
      },
      onSuccess: data => setLoraMessages(data.data?.map(getParsedMessages)),
      onSettled: () => setLastFetchTime(new Date()),
      refetchInterval: MESSAGES_REFETCH_INTERVAL_MS,
    },
  );

  return { loraMessages, loading: isLoading || isFetching, lastFetchTime };
};
